/* eslint-disable id-length */
import { Navigate, Outlet, useLocation, useMatches } from "react-router-dom";
import { useCallback, useEffect, useRef } from "react";
import PausedClusterQueuesNotice from "~/components/shared/PausedClusterQueuesNotice";
import BuildContentNavigation from "~/components/build/Show/components/BuildContentNavigation";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import Header from "~/components/build/Header";
import Sidebar from "~/components/build/Show/components/Sidebar";
import { PipelineInfo } from "~/components/build/Header/PipelineInfo";
import { PipelineSettingsMenu } from "~/components/build/Header/PipelineSettingsMenu";
import { Branch } from "~/components/build/Header/Branch";
import { Commit } from "~/components/build/Header/Commit";
import BuildDuration from "~/components/build/Duration";
import PipelineStateIcon, {
  VARIANTS,
} from "~/components/shared/PipelineStateIcon/PipelineStateIcon";
import { twMerge } from "tailwind-merge";
import { getBuildColor } from "~/components/build/Show/lib/getBuildColor";
import { CollapsedBuildActions } from "~/components/build/Show/components/CollapsedBuildActions";
import { Current } from "~/utils/Current";
import BuildNumber from "~/components/build/Header/BuildNumber";
import { PullRequest } from "~/components/build/Header/PullRequest";
import NewBuildUIToggleDialog from "~/components/build/Show/components/NewBuildUIToggleDialog";
import {
  clampSidebarWidth,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import { ResizeHandle } from "~/components/build/Show/components/ResizeHandle";

import classNames from "classnames";
import StepDrawer from "~/components/build/Show/components/StepDrawer";
import AnnotationsPanel from "~/components/build/Show/components/AnnotationsPanel";
import { useAnnotationsPanel } from "~/components/build/Show/lib/useAnnotationsPanel";
import { useAutoCloseAnnotationsPanelOnNarrow } from "~/components/build/Show/lib/useAutoCloseAnnotationsPanelOnNarrow";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { Revel } from "~/components/build/Show/components/Revel";
import debounce from "lodash/debounce";
import Icon from "~/components/shared/Icon";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import {
  useAdjustDockPositionForContainer,
  DockPosition,
} from "~/components/build/Show/lib/useAdjustedDockPosition";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import { useFocusOnFailure } from "~/components/build/Show/components/StepDrawer/GoToFailure/useFocusOnFailure";
import { useHotkeys, useHotkeysContext } from "react-hotkeys-hook";
import { LogLineSelectionProvider } from "~/components/build/Show/lib/useLogLineSelection";
import { CurrentStepSelectionContextProvider } from "~/components/build/Show/lib/useCurrentJobOrStep";
import { useStepDependenciesQuery } from "~/components/build/Show/lib/useStepDependencies";
import useBuildFavicon from "~/components/build/Show/lib/useBuildFavicon";
import { getJobForStep } from "~/components/build/Show/lib/getJobForStep";

import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useJobs } from "~/components/build/Show/lib/useJobs";
import { Type } from "~/lib/pipeline/Step";
import { BuildViewRedirect } from "~/components/build/Show";
import { convertModernToClassicParams } from "~/components/build/Show/lib/urlForView";

const PANEL_TRANSITION = {
  type: "spring",
  duration: 0.2,
};

/**
 * Converts BBUI-style `jid`/`sid` query params to Classic Build hash fragments.
 *
 * Renders inside ClassicBuildLayout so it intercepts all classic routes.
 * If a jid or sid param is present, redirects to the build root with the
 * appropriate hash fragment. Otherwise renders children normally.
 *
 * The `jid` case delegates directly to BuildViewRedirect (no store data needed).
 * The `sid` case mounts ClassicSidRedirect which subscribes to step/job stores
 * to resolve the step to a job UUID before redirecting.
 */
function ClassicParamRedirect({ children }: { children: React.ReactElement }) {
  const queryParams = useQueryParams();
  const matches = useMatches();

  // Canvas and Waterfall views natively handle sid/jid params, so skip redirects
  const currentViewId = [...matches].pop()?.id;
  const viewHandlesParams =
    currentViewId === BuildView.Canvas || currentViewId === BuildView.Waterfall;

  if (viewHandlesParams) {
    return children;
  }

  if (queryParams.jid) {
    return <BuildViewRedirect to={BuildView.Index} />;
  }

  if (queryParams.sid) {
    return <ClassicSidRedirect sid={queryParams.sid} />;
  }

  return children;
}

/**
 * Resolve a `sid` query param to a classic hash fragment.
 *
 * Subscribes to steps/jobs stores to look up the step and find its job UUID.
 * Only mounted when `sid` is actually present to avoid unnecessary store
 * subscriptions in the common case.
 */
function ClassicSidRedirect({ sid }: { sid: string }) {
  const { build } = useBuild();
  const { stepsMap, stepJobs } = useSteps();
  const jobs = useJobs();
  const location = useLocation();

  if (stepsMap.size === 0) {
    return null;
  }

  const step = stepsMap.get(sid);
  const stepJobsList = stepJobs.get(sid);

  if (step && stepJobsList) {
    const shouldRedirectToJob =
      step.type !== Type.Wait && step.type !== Type.Block && step.type !== Type.Input;

    if (shouldRedirectToJob) {
      const jobUuid = getJobForStep(step, stepJobsList, jobs);

      if (jobUuid) {
        // Preserve the existing hash which may contain line-range info (e.g. #jobUuid/2-15)
        const { hash } = convertModernToClassicParams(`?jid=${jobUuid}`, location.hash);
        return <Navigate to={`${build.path}${hash}`} replace={true} />;
      }
    }
  }

  // Step has no redirectable job (group/wait/block/input step or invalid sid).
  // Determine the target view from the current URL path and redirect there
  // without the sid param, preserving any hash fragment (e.g. line-range info).
  const pathSuffix = location.pathname.slice(build.path.length);
  const hash = location.hash || "";

  if (pathSuffix.endsWith("/canvas")) {
    return <Navigate to={`${build.path}/canvas${hash}`} replace={true} />;
  }
  if (pathSuffix.endsWith("/waterfall")) {
    return <Navigate to={`${build.path}/waterfall${hash}`} replace={true} />;
  }

  return <Navigate to={`${build.path}${hash}`} replace={true} />;
}

/**
 * The "classic" build layout, which is used when the new build UI is not enabled.
 *
 * @deprecated
 */
function ClassicBuildLayout() {
  const { build, setBuildData, reloadBuild } = useBuild();
  const matches = useMatches();
  const view = [...matches].pop()?.id as BuildView;

  return (
    <ClassicParamRedirect>
      <CurrentStepSelectionContextProvider>
        <PausedClusterQueuesNotice pausedQueues={build.dispatchPausedClusterQueues} />
        <Header
          build={build}
          showRebuild={true}
          setBuildData={setBuildData}
          reloadBuild={reloadBuild}
          currentView={view}
          showJobs={view === BuildView.Index}
        />
        <div className="mb-4 flex justify-between border-b border-gray-400 px-3 pr-0">
          <BuildContentNavigation />
          <NewBuildUIToggleDialog optIn={true} />
        </div>

        <Outlet />
      </CurrentStepSelectionContextProvider>
    </ClassicParamRedirect>
  );
}

function NextFailureHandler() {
  const { focusOnFailure } = useFocusOnFailure();
  useHotkeys("f", focusOnFailure);

  return null;
}

/**
 * BBUI Header
 */
function BuildHeader(props: { hideSidebar: boolean }) {
  const { build } = useBuild();
  const theme = getBuildColor(build);

  return (
    <div data-testid="bbui-build-header" className="flex flex-wrap gap-x-2 gap-y-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-2 sm:flex-nowrap">
        {props.hideSidebar ? null : <ToggleSidebarButton />}
        <PipelineInfo />

        <div className="flex min-w-0 flex-initial shrink-[2] items-center text-sm">
          <span className="hidden shrink-0 select-none px-1 text-gray-500 sm:!block">/</span>

          <Branch {...build} className="min-w-8 max-w-40 px-2" />

          {build.commitId && (
            <>
              <span className="shrink-0 select-none px-1 text-gray-500">/</span>

              <Commit {...build} className="min-w-8 max-w-40 px-2" />

              {build.pullRequest && (
                <span className="shrink-0 pr-2">
                  (<PullRequest {...build} />)
                </span>
              )}
            </>
          )}

          <span className="shrink-0 select-none px-1 text-gray-500">/</span>

          <div className="flex shrink-0 items-center gap-1 px-2 sm:mr-2">
            <BuildNumber {...build} />
          </div>
        </div>
      </div>

      <div className="flex flex-auto items-center justify-between gap-4">
        <div
          className={classNames("flex min-w-0 flex-initial shrink-0 items-center gap-1.5 text-sm", {
            "text-charcoal-800 font-medium tabular-nums": Features.BuildPageListView,
          })}
        >
          <PipelineStateIcon
            build={build}
            speedUpAnimationOnHover={true}
            style={{
              width: "20px",
              height: "20px",
              color: theme.primaryColor,
            }}
            variant={VARIANTS.large}
          />
          <BuildDuration build={build} />
        </div>

        <div className="flex min-h-[30px] min-w-0 items-center gap-1.5">
          <CollapsedBuildActions />
          {!Current.user.anonymous && <PipelineSettingsMenu />}
        </div>
      </div>
    </div>
  );
}

/**
 * Build layout (aka the "BBUI")
 */
function BuildLayout() {
  const { build } = useBuild();

  const { sid, jid, open } = useQueryParams();
  const stepOrJobId = sid || jid;

  const widthRef = useRef(useBuildPreferencesStore.getState().sidebarWidth);
  const setWidth = useBuildPreferencesStore((state) => state.setSidebarWidth);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  useAdjustDockPositionForContainer("#drawer-container");
  const dockPosition = useBuildPreferencesStore((state) => state.dockPosition);

  const { enableScope, disableScope } = useHotkeysContext();

  /**
   * Update the hotkey scope when the drawer is active
   */
  useEffect(() => {
    if (stepOrJobId && open !== "false") {
      enableScope("drawer");
    } else {
      disableScope("drawer");
    }
  }, [stepOrJobId, open, enableScope, disableScope]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSyncWidth = useCallback(debounce(setWidth, 250), []);

  const resizeSidebar = useCallback(
    (x: number) => {
      if (!sidebarRef.current || !handleRef.current) {
        return;
      }

      const offset =
        handleRef.current.getBoundingClientRect().left -
        sidebarRef.current.getBoundingClientRect().width;

      const width = clampSidebarWidth(x - offset);
      sidebarRef.current.style.width = `${width}px`;
      debounceSyncWidth(width);
      widthRef.current = width;
    },
    [debounceSyncWidth],
  );

  const hideSidebar = Features.HideBuildSidebar;
  const isSidebarOpen = useBuildPreferencesStore((state) =>
    hideSidebar ? false : state.isSidebarOpen,
  );
  const toggleSidebar = useBuildPreferencesStore((state) => state.toggleSidebar);

  useHotkeys("meta+b", toggleSidebar, { enabled: !hideSidebar });

  return (
    <CurrentStepSelectionContextProvider>
      <NextFailureHandler />
      <div className="relative -mt-2.5 flex min-w-0 flex-auto flex-col gap-2">
        <BuildHeader hideSidebar={hideSidebar} />

        <PausedClusterQueuesNotice pausedQueues={build.dispatchPausedClusterQueues} />

        <div className="flex min-w-0 flex-auto flex-row">
          <LayoutGroup>
            <AnimatePresence initial={false}>
              {isSidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, marginLeft: `-${widthRef.current}px` }}
                  animate={{ opacity: 1, marginLeft: 0 }}
                  exit={{ opacity: 0, marginLeft: `-${widthRef.current}px` }}
                  transition={PANEL_TRANSITION}
                  className="hidden min-w-0 flex-col gap-4 md:!flex"
                  style={{ width: `${widthRef.current}px` }}
                  ref={sidebarRef}
                >
                  <div className="relative mr-3 h-full">
                    <Sidebar />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div
              layout={true}
              id="drawer-container"
              transition={PANEL_TRANSITION}
              className={classNames(
                "@container/main relative flex min-w-0 flex-auto flex-row-reverse gap-2 md:min-w-[480px]",
                {
                  "flex-col-reverse": dockPosition === DockPosition.Bottom,
                },
              )}
            >
              {isSidebarOpen && (
                <ResizeHandle
                  direction="horizontal"
                  onResize={resizeSidebar}
                  ref={handleRef}
                  className="left-[1px] rounded-l-md"
                />
              )}
              {/* As an optimization, render the step drawer **before** the main outlet to handle cases with excessive DOM nodes */}
              {stepOrJobId && open !== "false" && <StepDrawer key={stepOrJobId} />}

              <div className="@container/outlet relative flex min-w-0 flex-auto flex-col rounded-md md:min-h-60 md:min-w-[480px]">
                <div className="flex min-w-0 flex-auto flex-col rounded-md border border-gray-400 bg-[#F8FAFC]">
                  <div
                    className={twMerge(
                      "shadow-xs flex border-b border-gray-400 px-2",
                      classNames({ "pr-2": Features.BuildSidebarToggle }),
                    )}
                  >
                    <BuildContentNavigation />
                  </div>

                  <Outlet />
                </div>
              </div>
            </motion.div>
          </LayoutGroup>
        </div>
      </div>
    </CurrentStepSelectionContextProvider>
  );
}

const ToggleSidebarButton = () => {
  const track = useTrack();
  const isSidebarOpen = useBuildPreferencesStore((state) => state.isSidebarOpen);
  const toggleSidebar = useBuildPreferencesStore((state) => state.toggleSidebar);

  const handleClick = useCallback(() => {
    toggleSidebar();
    track("Build Sidebar Toggled", { state: isSidebarOpen ? "collapsed" : "expanded" });
  }, [toggleSidebar, isSidebarOpen, track]);

  let userAgentMetaIcon = "⌘";
  if (typeof window !== "undefined") {
    userAgentMetaIcon = navigator.userAgent.includes("Mac") ? "⌘" : "Ctrl";
  }

  const hotKey = `${userAgentMetaIcon}+B`;

  return (
    <button
      data-testid="sidebar-toggle"
      onClick={handleClick}
      title={isSidebarOpen ? `Collapse sidebar (${hotKey})` : `Expand sidebar (${hotKey})`}
      className="hidden h-6 w-6 items-center justify-center rounded-sm bg-transparent transition-colors hover:bg-purple-100 hover:text-purple-600 md:!flex"
    >
      <Icon
        icon="custom/outline/sidebar-left"
        className={classNames("h-[18px] w-[18px]", {
          "[&>path:nth-child(2)]:fill-current [&>path:nth-child(n+3)]:stroke-white": isSidebarOpen,
        })}
      />
    </button>
  );
};

function ModernBuildLayout() {
  const { build } = useBuild();
  const { sid, jid, open } = useQueryParams();
  const { enableScope, disableScope } = useHotkeysContext();

  const dockPosition = useBuildPreferencesStore((state) => state.dockPosition);
  const { isOpen: annotationsPanelOpen } = useAnnotationsPanel();
  const showAnnotationsPanel = Features.BuildPageDockedAnnotations && annotationsPanelOpen;

  const stepOrJobId = sid || jid;

  useAdjustDockPositionForContainer("#drawer-container");
  useAutoCloseAnnotationsPanelOnNarrow("#panels-container");

  /**
   * Update the hotkey scope when the drawer is active
   */
  useEffect(() => {
    if (stepOrJobId && open !== "false") {
      enableScope("drawer");
    } else {
      disableScope("drawer");
    }
  }, [stepOrJobId, open, enableScope, disableScope]);

  return (
    <CurrentStepSelectionContextProvider>
      <NextFailureHandler />
      <div className="relative -mt-2.5 flex min-w-0 flex-auto flex-col gap-2">
        <BuildHeader hideSidebar={true} />

        <PausedClusterQueuesNotice pausedQueues={build.dispatchPausedClusterQueues} />

        <div
          id="panels-container"
          className="@container/panels relative flex min-w-0 flex-auto flex-col rounded-md border border-gray-400 bg-[#F8FAFC]"
        >
          <div className="flex flex-row items-center justify-between px-1">
            <BuildContentNavigation annotationsPanelOpen={showAnnotationsPanel} />
          </div>

          <div className="relative flex min-w-0 flex-auto flex-row gap-1 px-1">
            <div
              id="drawer-container"
              className={classNames(
                "@container/main relative mb-1 flex min-w-0 flex-auto flex-row-reverse gap-1 md:min-w-[480px]",
                {
                  "flex-col-reverse": dockPosition === DockPosition.Bottom,
                },
              )}
            >
              {/* As an optimization, render the step drawer **before** the main outlet to handle cases with excessive DOM nodes */}
              {stepOrJobId && open !== "false" && <StepDrawer key={stepOrJobId} />}

              <div
                className={classNames(
                  "@container/outlet relative flex min-w-0 flex-auto flex-col rounded-md md:min-h-60 md:min-w-[480px]",
                )}
              >
                <div
                  className={classNames(
                    "flex min-w-0 flex-auto flex-col overflow-hidden rounded-md border border-gray-400",
                    {
                      "bg-[#F8FAFC]": !Features.BuildPageListView,
                      "bg-white": Features.BuildPageListView,
                    },
                  )}
                >
                  <Outlet />
                </div>
              </div>
            </div>
            {showAnnotationsPanel && (
              <div className="@xl-bbui/panels:!flex mb-1 hidden min-w-0">
                <AnnotationsPanel />
              </div>
            )}
          </div>

          {/*
            Small-viewport "zoom" overlay. Positions over most of the
            @container/panels container (including the nav row) with a small
            inset so the page card is still visible behind. The in-panel
            close button replaces the toggle in the nav while this is open.

            z-40 sits above the centre-/right-docked drawer (z-30) so the
            overlay covers it, but below the fullscreen drawer (z-50, when
            dockPosition === Full) which should remain on top of everything.
          */}
          {showAnnotationsPanel && (
            <div className="@xl-bbui/panels:hidden absolute inset-1 z-40 flex *:!w-full">
              <AnnotationsPanel overlay={true} />
            </div>
          )}
        </div>
      </div>
    </CurrentStepSelectionContextProvider>
  );
}

export default function BuildShowLayout() {
  const { build } = useBuild();

  useStepDependenciesQuery(build);
  useBuildFavicon(build);

  // Modern Build Layout
  if (Features.BuildSidebar && Features.BuildPageListView) {
    return (
      <>
        <ModernBuildLayout />
        <Revel />
      </>
    );
  }

  // Better Build Layout
  if (Features.BuildSidebar) {
    return (
      <>
        <BuildLayout />
        <Revel />
      </>
    );
  }

  // Classic Experience Layout
  return (
    <LogLineSelectionProvider>
      <ClassicBuildLayout />
      <Revel />
    </LogLineSelectionProvider>
  );
}
